import type * as PdfLib from 'pdf-lib';
import type { Color, LineCapStyle as LineCapStyleEnum, PDFDocument, PDFPage } from 'pdf-lib';

import { inkToStrokes, type SignatureInk } from '@/components/Signature/signatureInk';
import { normalizeRotation } from '../pdfPageLayout';
import { rectFromScreen, rectToScreen, type FieldRect, type PageView } from './signField';

/**
 * Producing the signed document (FIL-8c).
 *
 * **The only module that imports `pdf-lib`**, and it imports it lazily — a
 * reader who never signs anything never downloads it.
 *
 * **The mark is drawn as vector, not as a picture.** This is the payoff of
 * ADR-0055's decision to store the pen path: each stroke segment becomes a
 * line in the page's content stream at its own thickness, so the signature is
 * as sharp at 800% as the text beside it and costs a few kilobytes rather than
 * an embedded image. A stored raster would have been resampled into whatever
 * box the reader drew.
 *
 * **It refuses two documents rather than damaging them** (FIL-8c). Encrypted:
 * `ignoreEncryption` is never passed, because stripping a document's protection
 * in order to mark it — silently, on somebody else's evidence — is not a thing
 * this system does. Already digitally signed: producing the new file rewrites
 * the whole document, which invalidates that signature, and handing a reader a
 * filing whose seal has quietly broken is worse than refusing.
 *
 * **The geometry is `signField.ts`'s, reused rather than restated.** A page
 * with its own `/Rotate` is displayed turned, so ink drawn axis-aligned in user
 * space appears sideways to the reader. Rather than write a second rotation
 * matrix here — and get it wrong in a second place — every point is laid out in
 * the orientation the reader saw and pushed back through the same inverse the
 * overlay uses, which has round-trip tests at all four quarters.
 */

export type SignFieldKind = 'signature' | 'text' | 'draw';

export interface SignField {
  id: string;
  kind: SignFieldKind;
  /** Zero-based. */
  pageIndex: number;
  /** In PDF points, against the unrotated page. */
  rect: FieldRect;
  /** Only meaningful for a `text` field. */
  text?: string;
  /**
   * The mark's own pen path. Only meaningful for a `draw` field — a freehand
   * drawing made on this document, which carries its own ink rather than the
   * account signature the `signature` kind shares.
   */
  ink?: SignatureInk;
}

export type SignFailure = 'encrypted' | 'already-signed' | 'no-fields' | 'failed';

export class SignError extends Error {
  readonly reason: SignFailure;

  constructor(reason: SignFailure, cause?: unknown) {
    super(`The document could not be signed (${reason}).`, cause === undefined ? {} : { cause });
    this.name = 'SignError';
    this.reason = reason;
  }
}

/**
 * Fold what a keyboard produces into what a standard PDF font can draw.
 *
 * **`StandardFonts.Helvetica` is WinAnsi-encoded and `drawText` throws on
 * anything outside cp1252** — including the curly apostrophe and the en dash
 * that macOS, Windows and Word all insert automatically. That failure would
 * arrive as an exception in the middle of saving, after the person had placed
 * everything, so the characters are folded to their ASCII equivalents on the
 * way in and anything still unrepresentable is dropped.
 *
 * The alternative is embedding a subsetted TTF, which means `@pdf-lib/fontkit`,
 * a font asset, and a licence question — for text that is nearly always a date.
 */
export function toWinAnsi(text: string): string {
  const folded = text
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    // A non-breaking space, which Word inserts and Helvetica has no glyph for.
    .replace(/\u00a0/g, ' ');

  // Printable ASCII plus the Latin-1 supplement, which is what WinAnsi covers
  // for our purposes. Escaped rather than written literally: the ranges contain
  // a non-breaking space and ESLint's irregular-whitespace rule cannot tell an
  // intentional one inside a character class from a stray one in code.
  // Anything outside is dropped rather than substituted — a box glyph in a
  // court filing is worse than an absence.
  return [...folded].filter((character) => /[ -~¡-ÿ]/.test(character)).join('');
}

/** What a text field's glyphs are sized at, as a fraction of its box height. */
const TEXT_SIZE_RATIO = 0.62;

export interface SignPdfInput {
  /** The original bytes. Not mutated; `PDFDocument.load` copies. */
  bytes: ArrayBuffer;
  fields: SignField[];
  /**
   * The account signature, shared by every `signature` field. Optional: a
   * document signed with only freehand drawings — which carry their own ink —
   * needs no stored mark, and a signer who never set one up can still draw.
   */
  ink?: SignatureInk | undefined;
}

export async function signPdf({ bytes, fields, ink }: SignPdfInput): Promise<Uint8Array> {
  if (fields.length === 0) throw new SignError('no-fields');

  const lib = await import('pdf-lib');
  const { PDFDocument, StandardFonts, rgb, degrees, LineCapStyle } = lib;

  let document: PDFDocument;
  try {
    // No `ignoreEncryption`. See the header — that flag is the whole refusal.
    document = await PDFDocument.load(bytes);
  } catch (caught) {
    const message = caught instanceof Error ? caught.message : '';
    if (/encrypt/i.test(message)) throw new SignError('encrypted', caught);
    throw new SignError('failed', caught);
  }

  if (hasSignatureField(document, lib)) throw new SignError('already-signed');

  const font = await document.embedFont(StandardFonts.Helvetica);
  const black = rgb(0, 0, 0);

  for (const field of fields) {
    const page = document.getPages()[field.pageIndex];
    if (page === undefined) continue;

    /*
      **The crop box, not the media box.** pdf.js builds its viewport from the
      page's *crop* box, so that is the space the overlay measured in; pdf-lib
      draws relative to the media box's origin. On a page where the two differ —
      a trimmed scan, most e-filed exhibits — placing against the media box puts
      every mark off by exactly the crop origin, consistently and invisibly
      until somebody compares the two documents.
    */
    const crop = page.getCropBox();
    const rotation = normalizeRotation(page.getRotation().angle);

    const view: PageView = {
      scale: 1,
      rotation,
      page: { width: crop.width, height: crop.height },
    };

    /** Where the field sits as the reader saw it, before the page's own turn. */
    const shown = rectToScreen(field.rect, view);

    /** A point inside the displayed box, as a point in user space. */
    const toUserSpace = (x: number, y: number) => {
      const point = rectFromScreen({ x, y, width: 0, height: 0 }, view);
      return { x: point.x + crop.x, y: point.y + crop.y };
    };

    if (field.kind === 'signature') {
      // A signature field with no account mark cannot be drawn; the toolbar
      // never arms one in that state, so this is a guard, not a path.
      if (ink !== undefined) {
        drawInk(page, ink, shown, toUserSpace, { black, lineCap: LineCapStyle.Round });
      }
      continue;
    }

    if (field.kind === 'draw') {
      // A freehand drawing carries its own ink. An empty one is skipped rather
      // than refused — `commitDrawing` already drops strokes too faint to store.
      if (field.ink !== undefined) {
        drawInk(page, field.ink, shown, toUserSpace, { black, lineCap: LineCapStyle.Round });
      }
      continue;
    }

    const text = toWinAnsi(field.text ?? '').trim();
    if (text === '') continue;

    const size = shown.height * TEXT_SIZE_RATIO;
    // The baseline, a little in from the box's leading edge and sitting on a
    // line that leaves room for descenders.
    const anchor = toUserSpace(shown.x + size * 0.15, shown.y + shown.height - size * 0.25);

    page.drawText(text, {
      x: anchor.x,
      y: anchor.y,
      size,
      font,
      color: black,
      // Counter the page's own turn, so the text reads upright to whoever the
      // viewer shows the rotated page to.
      rotate: degrees(-rotation),
    });
  }

  /*
    `useObjectStreams: false` keeps the output a plain cross-reference table
    rather than a compressed stream. It is larger by a little and readable by
    everything, including the older desktop viewers a clerk's office runs — and
    a document nobody can open is a worse outcome than a document a few
    kilobytes bigger.
  */
  try {
    return await document.save({ useObjectStreams: false });
  } catch (caught) {
    throw new SignError('failed', caught);
  }
}

/**
 * Whether the document already carries a digital signature.
 *
 * `/SigFlags` on the AcroForm is the indicator every producer sets — the
 * signature dictionaries themselves live on individual widget annotations and
 * finding them all means walking every page. This is the cheap check that
 * catches the case that matters: a document somebody has already sealed.
 */
function hasSignatureField(document: PDFDocument, lib: typeof PdfLib): boolean {
  try {
    const acroForm = document.catalog.lookupMaybe(lib.PDFName.of('AcroForm'), lib.PDFDict);
    const flags = acroForm?.lookupMaybe(lib.PDFName.of('SigFlags'), lib.PDFNumber);

    return (flags?.asNumber() ?? 0) > 0;
  } catch {
    // A document whose catalog cannot be walked is one `PDFDocument.load` was
    // willing to open; treat the absence of an answer as "no signature" rather
    // than refusing a document for a reason nobody could act on.
    return false;
  }
}

interface DrawContext {
  black: Color;
  lineCap: LineCapStyleEnum;
}

/**
 * The mark, one line per stroke segment.
 *
 * **Per-segment rather than one path**, because the pen's width varies along a
 * stroke and an SVG path carries a single stroke width. A signature is fifty to
 * two hundred points after simplification, so this is a couple of hundred tiny
 * operators — nothing beside the page it is being drawn on.
 */
function drawInk(
  page: PDFPage,
  ink: SignatureInk,
  shown: FieldRect,
  toUserSpace: (x: number, y: number) => { x: number; y: number },
  { black, lineCap }: DrawContext,
): void {
  // Fit the mark inside the box on its own aspect ratio, so a reader who
  // dragged a box of a different shape gets their signature rather than a
  // stretched one.
  const height = Math.min(shown.height, shown.width / ink.aspect);
  const width = height * ink.aspect;
  const offsetX = shown.x + (shown.width - width) / 2;
  const offsetY = shown.y + (shown.height - height) / 2;

  for (const stroke of inkToStrokes(ink, height)) {
    for (let i = 1; i < stroke.length; i += 1) {
      const from = stroke[i - 1];
      const to = stroke[i];
      if (from === undefined || to === undefined) continue;

      page.drawLine({
        start: toUserSpace(offsetX + from.x, offsetY + from.y),
        end: toUserSpace(offsetX + to.x, offsetY + to.y),
        thickness: Math.max(0.4, (from.w + to.w) / 2),
        color: black,
        lineCap,
      });
    }
  }
}
